import { Button, CircularProgress, Grid, Stack } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import {
  FormikDisabler,
  Panel,
  usePermissions,
} from "@poseidon/ui/components";
import Form from "@poseidon/ui/components/form/FullWidthForm";
import { Field, Formik } from "formik";
import { RadioGroup } from "formik-mui";
import { FileManagement } from "../../../Files";
import { AffordabilityCalculator } from "./AffordabilityCalculator";
import * as api from "../../api";
import NoteLogPanel from "../../../NoteLog/NoteLogPanel";
import { useCreditUnderwriting } from "../../hooks/useCreditUnderwriting";
import config from "@poseidon/utils/config";
import { useApplicationContext } from "../edit/ApplicationContext";
import { ApplicationHasAgreementWarning } from "../../components/ApplicationHasAgreementWarning";
import { isApplicationEditable } from "../../isApplicationEditable";
import React, { useState } from "react";
import DatePicker from "@poseidon/ui/components/form/DatePicker";
import { getDate } from "@poseidon/firebase/timestamp";

const noteLogMapper = (doc) => ({
  ...doc.data(),
  id: doc.id,
  createdAt: getDate(doc.get("createdAt")),
});

export const CreditUnderwritingSection = () => {
  const {
    getFileResolver,
    riskStatusOptions,
    passFailOptions,
    noteCategories,
  } = config;

  const { canWrite } = usePermissions();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const application = useApplicationContext();
  const [saving, setSaving] = useState(false);

  const { creditUnderwriting, loading, error } = useCreditUnderwriting(
    application.id
  );

  const addNoteHandler =
    ({ message, noteCategory }) =>
    (values, { resetForm }) => {
      try {
        api.addNote({
          ...values,
          application,
          noteCategory,
        });
        resetForm();
        setToast(message);
      } catch (error) {
        setError(error.message);
      }
    };

  const onCreditReportNotesAddHandler = addNoteHandler({
    message: "Added Credit Report Note",
    noteCategory: noteCategories.creditReportNote,
  });

  const onAffordabilityNotesAddHandler = addNoteHandler({
    message: "Added Affordability Note",
    noteCategory: noteCategories.affordabilityNote,
  });

  const onCreditSummaryNotesAddHandler = addNoteHandler({
    message: "Added Credit Summary Note",
    noteCategory: noteCategories.creditSummaryNote,
  });

  const onSubmitHandler = async (values) => {
    try {
      setSaving(true);
      await api.updateCreditUnderwriting(application.id, values);
      setToast("Updated Credit Underwriting");
    } catch (error) {
      setError(error.message);
    }
    setSaving(false);
  };

  if (error) {
    setError(error);
  }

  if (loading || !creditUnderwriting) {
    return <CircularProgress sx={{ alignSelf: "center" }} />;
  }
  const readonly = !isApplicationEditable(application);

  const { creditReportStatus, creditSummaryStatus, creditSummaryStatusExpiry } =
    creditUnderwriting;

  return (
    <Stack spacing={5} sx={{ mb: 5, mx: 5 }}>
      <ApplicationHasAgreementWarning application={application} />

      <Panel title="File Upload">
        <FileManagement
          fileCategory="application"
          resolveMetadata={getFileResolver("creditUnderwriting")}
          applicationId={application.id}
          readonly={readonly}
        />
      </Panel>

      <NoteLogPanel
        title="Credit Report Notes"
        dbPathSlugs={[
          "applications",
          application.id,
          noteCategories.creditReportNote,
        ]}
        updateHandler={onCreditReportNotesAddHandler}
        mapper={noteLogMapper}
      />

      <Panel title="Credit Report">
        <Formik
          initialValues={{
            creditReportStatus: creditReportStatus ?? "pending",
          }}
          enableReinitialize
          onSubmit={onSubmitHandler}
        >
          {({ dirty, isSubmitting }) => (
            <Form>
              <FormikDisabler disabled={readonly || saving || !canWrite} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field component={RadioGroup} name="creditReportStatus">
                    {riskStatusOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio disabled={isSubmitting} />}
                        label={option.label}
                        disabled={isSubmitting}
                      />
                    ))}
                  </Field>
                </Grid>

                {dirty && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      margin="normal"
                    >
                      Save Status
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </Panel>

      <NoteLogPanel
        title="Affordability Notes"
        dbPathSlugs={[
          "applications",
          application.id,
          noteCategories.affordabilityNote,
        ]}
        updateHandler={onAffordabilityNotesAddHandler}
        mapper={noteLogMapper}
      />

      <AffordabilityCalculator application={application} />

      <NoteLogPanel
        title="Credit Summary Notes"
        dbPathSlugs={[
          "applications",
          application.id,
          noteCategories.creditSummaryNote,
        ]}
        updateHandler={onCreditSummaryNotesAddHandler}
        mapper={noteLogMapper}
      />

      <Panel title="Credit Summary">
        <Formik
          initialValues={{
            creditSummaryStatus: creditSummaryStatus ?? "pending",
            creditSummaryStatusExpiry: creditSummaryStatusExpiry ?? null,
          }}
          enableReinitialize
          onSubmit={onSubmitHandler}
        >
          {({ dirty, isSubmitting, values, setFieldValue }) => (
            <Form>
              <FormikDisabler disabled={readonly || saving || !canWrite} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    component={RadioGroup}
                    name="creditSummaryStatus"
                    onChange={(ev, value) => {
                      setFieldValue("creditSummaryStatus", value);
                      if (value === "pass") {
                        const d = new Date();
                        d.setMonth(d.getMonth() + 3);
                        setFieldValue("creditSummaryStatusExpiry", d);
                      } else {
                        setFieldValue("creditSummaryStatusExpiry", null);
                      }
                    }}
                  >
                    {passFailOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio disabled={isSubmitting} />}
                        label={option.label}
                        disabled={isSubmitting}
                      />
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    component={DatePicker}
                    name="creditSummaryStatusExpiry"
                    label="Decision Expiry"
                    margin="normal"
                    disabled={values.creditSummaryStatus !== "pass"}
                  />
                </Grid>
                {dirty && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      margin="normal"
                    >
                      Save Status
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </Panel>
    </Stack>
  );
};
